// src/contexts/cartStorage.js
//
// Persistence layer for the kiosk cart. The cart used to live only in React
// in-memory state, so any full-page reload (iPad Safari reaping a backgrounded
// tab, pull-to-refresh, the ErrorBoundary "Reload Page" button, memory
// pressure) wiped it. These helpers save the cart per store and restore it on
// mount so it survives a reload. (TKT-2026-103200)

const KEY_PREFIX = 'kiosk-cart-';

/**
 * Map a router pathname to the storage key for that store's cart.
 * The store slug is the first path segment, so `/brt` and `/brt/cart` share one
 * key while different stores stay isolated. The landing route has no store.
 */
export function deriveStorageKey(pathname) {
  const firstSegment = String(pathname || '').replace(/^\/+/, '').split('/')[0];
  if (!firstSegment || firstSegment === 'select-store') {
    return `${KEY_PREFIX}session`;
  }
  return `${KEY_PREFIX}${firstSegment}`;
}

/**
 * Drop anything that would break the cart UI (missing variant/skuId) and
 * normalise qty/price, while preserving every other field so both sell-mode and
 * buy-mode items round-trip intact (mode, paymentType, buylist prices, etc.).
 * Quantity is NOT clamped to stock here: buy-mode items have no stock cap, and
 * the browsing UI already enforces the sell cap when quantity is incremented.
 */
export function sanitizeCartItems(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(item => {
      if (!item || typeof item !== 'object') return null;
      const variant = item.variant;
      if (!variant || typeof variant !== 'object' || variant.skuId == null) return null;
      const qty = Number(item.qty);
      const price = Number(item.price);
      return {
        ...item,
        qty: Number.isFinite(qty) && qty >= 1 ? Math.floor(qty) : 1,
        price: Number.isFinite(price) ? price : 0,
      };
    })
    .filter(Boolean);
}

function getStore() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  } catch {
    // Safari private mode can throw on access
  }
  return null;
}

/**
 * Read and sanitize the persisted cart for a store. Any problem (no storage,
 * missing key, corrupt JSON) yields an empty cart rather than throwing.
 */
export function loadCart(key) {
  const store = getStore();
  if (!store || !key) return [];
  try {
    const stored = store.getItem(key);
    if (!stored) return [];
    return sanitizeCartItems(JSON.parse(stored));
  } catch {
    return [];
  }
}

/**
 * Persist the cart for a store. Storage failures (quota, private mode) are
 * swallowed so a persistence error can never break the shopping flow.
 */
export function saveCart(key, cart) {
  const store = getStore();
  if (!store || !key) return;
  try {
    store.setItem(key, JSON.stringify(Array.isArray(cart) ? cart : []));
  } catch {
    // ignore persistence errors
  }
}
